// ===========================================
// Book Extra Lesson Dialog (admin on behalf of family)
// ===========================================
// Standalone dialog for admins to manually create an extra lesson booking.
// Used by ExtraLessonRequestsPage and the admin Calendar toolbar.

import { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import {
  useAdminBookExtraLesson,
  useAdminStudentMakeupOptions,
  extraLessonKeys,
  slotDateKey,
} from '../../hooks/useExtraLessonRequests';
import {
  AdminBookOnBehalfInput,
  AvailableSlot,
  StudentMakeupOption,
  extraLessonApi,
} from '../../api/booking.api';
import { formatUTCDate, localDateFromKey } from '../../utils/dateUtils';
import { teachersApi, Teacher, studentsApi, Student } from '../../api/users.api';
import { userKeys } from '../../hooks/useUsers';
import {
  roomsApi,
  Room,
  instrumentsApi,
  Instrument,
  lessonTypesApi,
  LessonType,
} from '../../api/admin.api';
import HistoryIcon from '@mui/icons-material/History';
import { useTerms } from '../../hooks/useAdmin';
import { getBookableTerms, getRunningTerm } from '../../utils/bookableTerms';
import { slotDayIsPastForSchool } from '../../utils/slotFilters';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { roomResourceTitle } from './calendar/calendar.helpers';

// ===========================================
// Shared scheduler types (also used by ExtraLessonRequestsPage's ScheduleDialog)
// ===========================================

export interface SchedulingState {
  teacherId: string;
  selectedDate: Date | null;
  selectedTime: string;
  duration: number;
  roomId: string;
  /**
   * Which days the tiles offer when the scheduler is given `termsForTiles`:
   * 'holidays' = only SCHOOL-HOLIDAY days (the "Out of term" switch ON);
   * absent/'term' = term days. The same tiles + slot grid either way.
   */
  mode?: 'term' | 'holidays';
}

export const emptyScheduling: SchedulingState = {
  teacherId: '',
  selectedDate: null,
  selectedTime: '',
  duration: 30,
  roomId: '',
};

interface TeacherAvailabilitySchedulerProps {
  value: SchedulingState;
  onChange: (next: SchedulingState) => void;
  disabled?: boolean;
  /**
   * Opt IN to selecting dates that have already passed (recording a lesson that
   * was taught but never entered — MNM request 3).
   *
   * ⚠️ **EXPLICIT, and defaulting to false, on purpose.** The first cut inferred
   * this from `currentTermStart` being present, which made the ABSENT case fall
   * through to a 60-day past window — so the two `ExtraLessonRequestsPage` sites
   * silently gained past dates, and one of them RESCHEDULES and emails the
   * family, with no audit row behind it. A capability this consequential must be
   * asked for, never inherited from the shape of another prop. (`/qa`, 2026-09-19.)
   */
  allowPastDates?: boolean;
  /**
   * ISO start date of the school's CURRENT term, when one is running. With
   * `allowPastDates`, it is the back edge of the window: an admin may record a
   * lesson that already happened, but only within the term still open for
   * reconciliation — "once a new term starts they can't edit the previous term"
   * (Andrew, 2026-09-19). Ignored unless `allowPastDates` is set.
   */
  currentTermStart?: string | null;
  /**
   * Lock the teacher selection (used for reschedule, where the backend keeps the
   * existing teacher). Date/time/room stay editable; only the teacher picker is
   * disabled so the admin can't pick a teacher that won't actually be applied.
   */
  lockTeacher?: boolean;
  /**
   * Opt IN to the "Out of term (school holidays)" split (holiday make-ups, MNM
   * 2026-09-28). When given, the date tiles offer TERM days (the running and
   * upcoming terms), and an "Out of term" switch flips them to the
   * SCHOOL-HOLIDAY days the teacher is available — the same tiles and slot grid
   * either way (Andrew: "the same picker we normally use and only display the
   * holiday dates the teacher is available").
   *
   * ⚠️ **Absent (the default), the scheduler renders exactly as it always has —
   * no switch, and the tiles are NOT term-filtered.** Only the admin "Book Extra
   * Class" dialog passes it; the Schedule and Reschedule dialogs on
   * ExtraLessonRequestsPage do not, and filtering there would remove holiday
   * dates with no way back to them. A capability must be asked for, never
   * inferred from another prop (see `allowPastDates`).
   */
  termsForTiles?: Array<{ startDate: string; endDate: string }>;
  /**
   * The last day the chosen make-up credit may be used (a LOCAL-midnight Date,
   * null = no limit). Holiday tiles stop there. Ignored without `termsForTiles`.
   */
  holidayLastDay?: Date | null;
}

/** How far ahead HOLIDAY tiles look: the next holidays can be a term away. */
const HOLIDAY_HORIZON_DAYS = 180;

export function TeacherAvailabilityScheduler({
  value,
  onChange,
  disabled,
  lockTeacher,
  allowPastDates,
  currentTermStart,
  termsForTiles,
  holidayLastDay = null,
}: TeacherAvailabilitySchedulerProps) {
  const holidays = !!termsForTiles && value.mode === 'holidays';
  const { data: teachers = [] } = useQuery({
    // Shares cache with useTeachers() so roster mutations reach it. A bare
    // ['teachers'] key is a DISJOINT key space from ['users','teachers'] —
    // React Query prefix-matches from index 0 — so it was unreachable by
    // every existing userKeys.teachers() invalidation.
    queryKey: userKeys.teachers(),
    queryFn: () => teachersApi.getAll(),
  });

  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms'],
    queryFn: () => roomsApi.getAll(),
  });

  const { data: availableDays = [], isFetching: loadingDays } = useQuery({
    queryKey: extraLessonKeys.adminAvailabilityDays(value.teacherId),
    queryFn: () => extraLessonApi.adminGetTeacherAvailabilityDays(value.teacherId),
    enabled: !!value.teacherId,
  });

  const { data: availableSlots = [], isFetching: loadingSlots } = useQuery({
    // Key on the LOCAL YYYY-MM-DD the API actually queries — see slotDateKey.
    queryKey: extraLessonKeys.adminAvailableSlots(
      value.teacherId,
      value.selectedDate ? slotDateKey(value.selectedDate) : undefined,
      value.duration,
    ),
    queryFn: () =>
      extraLessonApi.adminGetAvailableSlots(
        value.teacherId,
        value.selectedDate!,
        value.duration,
      ),
    enabled: !!value.teacherId && !!value.selectedDate,
  });

  // ⚠️ The window OPENS BACKWARDS as well as forwards (MNM request 3,
  // 2026-09-19): "EJ had an extra lesson last week but forgot to put it on the
  // calendar, can we be able to add that in". The server has always accepted
  // this on the ADMIN path — `adminCreateScheduledRequest` has no past guard in
  // the service or the validator (the "Cannot book lessons in the past" error is
  // in `createDirectBooking`, the PARENT path, where it is correct). The only
  // thing that blocked it was this list starting at tomorrow.
  //
  // ⚠️ THE PAST WINDOW IS BOUNDED, and the bound is a business rule, not a
  // technical limit (Andrew, 2026-09-19): "once a new term starts they can't edit
  // the previous term — that allows them to clean up their records before the new
  // term." The term boundary IS the reconciliation deadline, and it is what keeps
  // the retro-edit audit log meaningful — unbounded back-dating would make the
  // supervision report unfalsifiable. So we walk back only as far as the CURRENT
  // term's start, never into an ended term.
  //
  // A back-dated booking writes a retro-edit audit row (`EXTRA_LESSON_BOOKED`,
  // gated on the SCHOOL's day) — see `extraLesson.service.ts`.
  const { dates: availableDates, pastKeys: pastDateKeys } = useMemo<{
    dates: Date[];
    pastKeys: Set<string>;
  }>(() => {
    if (!value.teacherId || availableDays.length === 0) {
      return { dates: [], pastKeys: new Set<string>() };
    }
    const today = startOfDay(new Date());

    // ⚠️ PAST AND FUTURE ARE BUILT SEPARATELY, WITH THEIR OWN CAPS. One list
    // walked forward from a back edge under a single cap looks equivalent and is
    // not: the past fills the quota FIRST, so the cap truncates the tail — and
    // the tail is the future. Measured (`/qa`, 2026-09-19) against a 10-week term
    // in its 11th week: a Mon–Fri teacher produced 40 past dates and **ZERO
    // future ones**, the last tile a fortnight in the past. Raising the cap does
    // not fix that — the split does, because the future count then cannot depend
    // on how many days a week the teacher works or how far into the term it is.
    const FUTURE_LIMIT = 14; // the pre-existing forward budget, unchanged
    const PAST_LIMIT = 12;

    // Starts TOMORROW, exactly as before this feature — the forward half must be
    // byte-for-byte the old behaviour for the two sites that do not opt in, or
    // this change quietly alters booking flows it has no business touching.
    const future: Date[] = [];
    // `YYYY-MM-DD` keys of each term's first and last day. Only the dialog with
    // the "Out of term" switch passes terms: there the tiles are TERM days, or —
    // switched on — the SCHOOL-HOLIDAY days (Andrew, 28/09/2026: with no split,
    // the tiles offered 29/09–11/10 while the switch was off, which made it look
    // pointless). Holiday tiles look further ahead (the next holidays can be a
    // term away) and stop at the make-up credit's last valid day.
    const termKeys = termsForTiles?.map((t) => [t.startDate.slice(0, 10), t.endDate.slice(0, 10)]);
    const inTerm = (d: Date) => {
      const key = slotDateKey(d);
      return termKeys!.some(([start, end]) => key >= start && key <= end);
    };
    const maxForward = addDays(today, holidays ? HOLIDAY_HORIZON_DAYS : 60);
    for (
      let c = addDays(today, 1);
      c <= maxForward && future.length < FUTURE_LIMIT;
      c = addDays(c, 1)
    ) {
      if (holidays && holidayLastDay && c > holidayLastDay) break;
      if (!availableDays.includes(c.getDay())) continue;
      if (termKeys && (holidays ? inTerm(c) : !inTerm(c))) continue;
      future.push(c);
    }

    // The past half is OPT-IN (`allowPastDates`) and bounded by the CURRENT
    // term — the reconciliation deadline, a business rule rather than a
    // technical limit: "once a new term starts they can't edit the previous
    // term, that allows them to clean up their records before the new term"
    // (Andrew, 2026-09-19). No running term (the holiday gap) means no term
    // authorises any back-dating, so the past half stays EMPTY rather than
    // falling back to a rolling window into an ended term.
    // Never a past day in HOLIDAY mode: the past window is the RUNNING term's,
    // and a past holiday day is the ended term's history (read-only).
    if (holidays) return { dates: future, pastKeys: new Set<string>() };

    const past: Date[] = [];
    if (allowPastDates && currentTermStart) {
      const termStart = startOfDay(new Date(currentTermStart));
      if (termStart < today) {
        // Walk BACKWARDS from yesterday so the nearest days survive the cap —
        // "last Tuesday" is the case this exists for, not week 1 of the term.
        for (let c = addDays(today, -1); c >= termStart && past.length < PAST_LIMIT; c = addDays(c, -1)) {
          if (availableDays.includes(c.getDay())) past.push(c);
        }
        past.reverse(); // chronological, so the strip reads left-to-right
      }
    }

    return { dates: [...past, ...future], pastKeys: new Set(past.map(slotDateKey)) };
  }, [value.teacherId, availableDays, allowPastDates, currentTermStart, termsForTiles, holidays, holidayLastDay]);

  const selectedTeacher =
    teachers.find((t: Teacher) => t.id === value.teacherId) || null;
  const selectedTeacherName = selectedTeacher
    ? `${selectedTeacher.User.firstName} ${selectedTeacher.User.lastName}`
    : 'The teacher';
  const selectedRoom = rooms.find((r: Room) => r.id === value.roomId) || null;

  const set = (patch: Partial<SchedulingState>) => onChange({ ...value, ...patch });

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Autocomplete
        options={teachers}
        getOptionLabel={(t: Teacher) => `${t.User.firstName} ${t.User.lastName}`}
        value={selectedTeacher}
        onChange={(_, teacher) =>
          set({
            teacherId: teacher?.id || '',
            selectedDate: null,
            selectedTime: '',
          })
        }
        disabled={disabled || lockTeacher}
        renderInput={(params) => <TextField {...params} label="Teacher" required />}
        fullWidth
      />

      {termsForTiles && (
        <FormControlLabel
          control={
            <Switch
              checked={holidays}
              onChange={(e) =>
                set({
                  mode: e.target.checked ? 'holidays' : 'term',
                  selectedDate: null,
                  selectedTime: '',
                })
              }
              disabled={disabled}
            />
          }
          label="Out of term (school holidays) — show the teacher's holiday dates"
        />
      )}

      <TextField
        type="number"
        label="Duration (minutes)"
        value={value.duration}
        onChange={(e) =>
          set({
            duration: parseInt(e.target.value) || 30,
            selectedDate: null,
            selectedTime: '',
          })
        }
        inputProps={{ min: 15, step: 15 }}
        disabled={disabled}
        fullWidth
        required
      />

      {value.teacherId && (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            {holidays ? 'Available Holiday Dates' : 'Available Dates'}
          </Typography>
          {loadingDays ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availableDates.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {!termsForTiles || availableDays.length === 0
                ? 'No availability configured for this teacher.'
                : holidays
                  ? holidayLastDay
                    ? `No school-holiday days for ${selectedTeacherName} before this make-up credit's last day (${format(holidayLastDay, 'dd/MM/yyyy')}).`
                    : `No school-holiday days coming up on ${selectedTeacherName}'s usual days.`
                  : 'No term days coming up for this teacher. For a school-holiday date, switch on "Out of term" above.'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {availableDates.map((date) => {
                const selected =
                  value.selectedDate && isSameDay(value.selectedDate, date);
                // A past tile is SELECTABLE but must not look like an ordinary
                // one: with the window open backwards the strip can be half
                // history, and an admin scanning for "Wed 9" otherwise has no
                // cue which side of today it falls on — the warning Alert only
                // appears after a click. Mirrors `WeekTilePicker`'s existing
                // treatment of a selectable past week (dashed, warning-coloured)
                // rather than inventing a third visual language. (`/qa` request
                // 3, 2026-09-19.)
                const isPastTile = pastDateKeys.has(slotDateKey(date));
                return (
                  <Paper
                    key={date.toISOString()}
                    data-testid={`date-tile-${slotDateKey(date)}`}
                    onClick={() =>
                      !disabled && set({ selectedDate: date, selectedTime: '' })
                    }
                    title={isPastTile ? 'This date has already passed — booking it records history' : undefined}
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      cursor: disabled ? 'default' : 'pointer',
                      minWidth: 64,
                      border: 2,
                      borderStyle: isPastTile && !selected ? 'dashed' : 'solid',
                      borderColor: selected
                        ? 'primary.main'
                        : isPastTile
                          ? 'warning.main'
                          : 'divider',
                      bgcolor: selected ? 'primary.main' : 'background.paper',
                      color: selected ? 'white' : 'text.primary',
                      opacity: isPastTile && !selected ? 0.75 : 1,
                      transition: 'all 0.2s',
                      '&:hover': disabled
                        ? {}
                        : {
                            borderColor: 'primary.main',
                            bgcolor: selected ? 'primary.main' : 'action.hover',
                          },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        opacity: 0.8,
                        display: 'block',
                      }}
                    >
                      {format(date, 'EEE')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {format(date, 'd')}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {format(date, 'MMM')}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </>
      )}

      {value.selectedDate && (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            Available Times — {format(value.selectedDate, 'EEEE, d MMMM')}
          </Typography>
          {loadingSlots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availableSlots.length === 0 ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              No available slots on this day.
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 0.5,
              }}
            >
              {availableSlots.map((slot: AvailableSlot) => {
                const selected = value.selectedTime === slot.startTime;
                return (
                  <Paper
                    key={slot.startTime}
                    onClick={() =>
                      !disabled &&
                      slot.isAvailable &&
                      set({ selectedTime: slot.startTime })
                    }
                    elevation={0}
                    sx={{
                      py: 0.75,
                      px: 0.5,
                      textAlign: 'center',
                      cursor:
                        slot.isAvailable && !disabled ? 'pointer' : 'not-allowed',
                      opacity: slot.isAvailable ? 1 : 0.45,
                      border: 1.5,
                      borderColor: selected ? 'primary.main' : 'rgba(0,0,0,0.12)',
                      bgcolor: selected ? 'primary.main' : 'transparent',
                      color: selected ? 'white' : 'text.primary',
                      transition: 'all 0.2s',
                      '&:hover':
                        slot.isAvailable && !disabled
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: selected ? 'primary.main' : 'action.hover',
                            }
                          : {},
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    >
                      {format(new Date(`2000-01-01T${slot.startTime}`), 'h:mm a')}
                    </Typography>
                    {!slot.isAvailable && (
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, fontSize: '0.6rem', display: 'block' }}
                      >
                        {slot.reason === 'OVERLAPS' ? 'Overlaps' : 'Booked'}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </>
      )}

      <Autocomplete
        options={rooms}
        getOptionLabel={(r: Room) => roomResourceTitle(r)}
        value={selectedRoom}
        onChange={(_, room) => set({ roomId: room?.id || '' })}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label="Room (optional)" />}
        fullWidth
      />
    </Box>
  );
}

// ===========================================
// Book Extra Lesson Dialog
// ===========================================

export interface BookExtraLessonDialogPrefill {
  date?: Date;
  startTime?: string;
}

export interface BookExtraLessonDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: BookExtraLessonDialogPrefill;
}

/**
 * The pre-filled reason. Honest for an ordinary forward booking — an admin
 * booking on a family's behalf IS the reason — but it also happens to be 15
 * characters, so it satisfies the 10-char minimum without saying anything.
 *
 * ⚠️ Named rather than inlined so `handleSubmit` can tell "the admin left the
 * default" from "the admin typed something". A back-dated booking is rejected
 * while this is still the value: that row goes to the owner's retro-edit report,
 * where `reason` is the only field separating evidence from a bare count.
 */
const DEFAULT_BOOKING_REASON = 'Booked by admin';

const emptyBookDetails = {
  studentId: '',
  instrumentId: '',
  lessonTypeId: '',
  reason: DEFAULT_BOOKING_REASON,
  notes: '',
  useMakeupCredit: false,
  // Holiday make-ups (2026-09-28): the class the make-up replaces — its term is
  // the term the credit comes from, which is what allows a holiday booking.
  makeupForLessonId: '',
  // Staff-only (admin + teacher); REQUIRED when the duration isn't the class length.
  staffNote: '',
  shareNoteWithFamily: false,
};

/** `YYYY-MM-DD` → `dd/mm/yyyy` (CLAUDE.md Critical Rule 7). */
const ddmmyyyy = (key: string) =>
  formatUTCDate(key, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU');

const makeupOptionLabel = (o: StudentMakeupOption) =>
  `${o.lessonName} · ${o.termName} · ${o.creditsRemaining} credit${o.creditsRemaining === 1 ? '' : 's'} left` +
  (o.creditValidUntil ? ` · valid until ${ddmmyyyy(o.creditValidUntil)}` : '');

export default function BookExtraLessonDialog({
  open,
  onClose,
  prefill,
}: BookExtraLessonDialogProps) {
  const [details, setDetails] = useState(emptyBookDetails);
  const [scheduling, setScheduling] = useState<SchedulingState>(emptyScheduling);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const bookMutation = useAdminBookExtraLesson();

  // The term currently running bounds how far back the date list may go — the
  // reconciliation deadline, not a technical limit (see `availableDates`).
  const { data: terms } = useTerms();
  const schoolTimeZone = useSchoolTimezone();
  // D11: "has the term finished?" is the SCHOOL's day, not the UTC instant (a term ending today
  // used to drop out at 11:00 Sydney on its last day).
  const bookableTerms = useMemo(
    () => getBookableTerms(terms, new Date(), schoolTimeZone),
    [terms, schoolTimeZone]
  );
  const runningTerm = getRunningTerm(bookableTerms, new Date(), schoolTimeZone);

  // Is the chosen date already past FOR THE SCHOOL? Reuses the same predicate
  // and the same school-zone anchor as the clinic surfaces — a UTC anchor is
  // silent for ~10 hours every Sydney morning, which is exactly when an admin
  // records yesterday's lesson.
  const selectedDateIsPast = useMemo(
    () =>
      slotDayIsPastForSchool(
        // ⚠️ `slotDateKey` (LOCAL getters), NOT `toUTCDateKey`. The tiles in
        // `availableDates` are built at LOCAL midnight, so for any viewer east of
        // UTC — i.e. every MNM user — `toUTCDateKey` reads them back as the
        // PREVIOUS day (Sydney local midnight is 14:00Z the day before). The
        // notice would then judge a different day from the one the admin clicked,
        // and a different day from the one actually submitted: the submit path
        // uses `format(date, 'yyyy-MM-dd')`, which is local too. Caught by /qa,
        // 2026-09-19. One helper for one Date shape — see `slotDateKey`'s note.
        scheduling.selectedDate ? { dateKey: slotDateKey(scheduling.selectedDate) } : null,
        schoolTimeZone
      ),
    [scheduling.selectedDate, schoolTimeZone]
  );

  // Apply prefill when the dialog opens with a fresh prefill payload.
  useEffect(() => {
    if (!open || !prefill?.date) return;
    setScheduling((prev) => ({
      ...prev,
      selectedDate: prefill.date ?? prev.selectedDate,
      selectedTime: prefill.startTime ?? prev.selectedTime,
    }));
  }, [open, prefill?.date, prefill?.startTime]);

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentsApi.getAll(),
    enabled: open,
  });

  const { data: instruments = [] } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.getAll(),
    enabled: open,
  });

  const { data: lessonTypes = [] } = useQuery({
    queryKey: ['lessonTypes'],
    queryFn: () => lessonTypesApi.getAll(),
    enabled: open,
  });

  // The classes a make-up can be booked FOR, each with ITS term's credit balance
  // and last valid day — the running term, or the one just ended during the
  // holidays (holiday make-ups, 2026-09-28).
  const { data: makeupOptions = [], isFetching: creditsLoading } =
    useAdminStudentMakeupOptions(details.studentId);
  const hasCredits = makeupOptions.some((o) => o.creditsRemaining > 0);
  const selectedMakeup =
    makeupOptions.find((o) => o.lessonId === details.makeupForLessonId) ?? null;
  // A make-up that is not the class length needs a reason the teacher can see —
  // the server enforces the same rule against the class length IT derives.
  const durationDiffers =
    details.useMakeupCredit &&
    !!selectedMakeup &&
    scheduling.duration !== selectedMakeup.defaultDurationMins;

  // If the student changes to one with no credits, never leave the toggle on.
  useEffect(() => {
    if (!hasCredits && details.useMakeupCredit) {
      setDetails((prev) => ({ ...prev, useMakeupCredit: false, makeupForLessonId: '' }));
    }
  }, [hasCredits, details.useMakeupCredit]);

  /** Choosing the class sets the make-up's default length and, if unset, its teacher. */
  const selectMakeupClass = (option: StudentMakeupOption | null) => {
    setDetails((prev) => ({ ...prev, makeupForLessonId: option?.lessonId ?? '' }));
    setErrors((prev) => ({ ...prev, makeupForLessonId: '' }));
    if (option) {
      setScheduling((prev) => ({
        ...prev,
        duration: option.defaultDurationMins,
        ...(prev.teacherId
          ? // The chosen time came from a slot grid built for the OLD duration —
            // drop it, exactly as the Duration field does (/qa L1).
            prev.duration !== option.defaultDurationMins
            ? { selectedTime: '' }
            : {}
          : // No teacher yet: take the class's. A date/time already here came from
            // a Calendar-cell prefill (tiles need a teacher), so keep it (/qa L5).
            { teacherId: option.teacherId }),
      }));
    }
  };

  // Exactly one class with credits → pick it for the admin.
  useEffect(() => {
    if (!details.useMakeupCredit || details.makeupForLessonId) return;
    const withCredits = makeupOptions.filter((o) => o.creditsRemaining > 0);
    if (withCredits.length === 1) selectMakeupClass(withCredits[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.useMakeupCredit, details.makeupForLessonId, makeupOptions]);

  // Holiday tiles stop at the chosen credit's last valid day (the day before the
  // next term starts) — a later tile would only be refused by the server. LOCAL
  // midnight, to compare with the LOCAL-midnight tiles.
  const holidayLastDay = useMemo(
    () => (selectedMakeup?.creditValidUntil ? localDateFromKey(selectedMakeup.creditValidUntil) : null),
    [selectedMakeup?.creditValidUntil]
  );

  const selectedStudent =
    students.find((s: Student) => s.id === details.studentId) || null;
  const selectedInstrument =
    instruments.find((i: Instrument) => i.id === details.instrumentId) || null;
  const selectedLessonType =
    lessonTypes.find((lt: LessonType) => lt.id === details.lessonTypeId) || null;

  const handleClose = () => {
    setDetails(emptyBookDetails);
    setScheduling(emptyScheduling);
    setErrors({});
    onClose();
  };

  const canSubmit =
    !!details.studentId &&
    !!scheduling.teacherId &&
    !!scheduling.selectedDate &&
    !!scheduling.selectedTime &&
    (!details.useMakeupCredit || !!selectedMakeup);

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!details.studentId) newErrors.studentId = 'Please select a student';
    if (!details.reason.trim() || details.reason.trim().length < 10)
      newErrors.reason = 'Reason must be at least 10 characters';
    // ⚠️ A BACK-DATED booking must carry a REAL reason, not the default.
    // `reason` is the one field that makes the retro-edit report readable rather
    // than a bare count — "recorded 9 days late: Booked by admin" tells the
    // owner nothing, and this path is the only retro surface that has the field
    // at all. The default stays valid for an ordinary forward booking, where
    // "Booked by admin" is genuinely the reason. (`/qa` request 3, 2026-09-19.)
    else if (selectedDateIsPast && details.reason.trim() === DEFAULT_BOOKING_REASON)
      newErrors.reason =
        'This date has already passed — say why it is being recorded now (it appears in the retroactive edit report).';
    const useCredit = details.useMakeupCredit && hasCredits;
    // Gate on the RESOLVED option, not the raw id: a stale id (another student's
    // class, or one a refetch dropped) resolves to nothing (/qa Phase 6 M1).
    if (useCredit && !selectedMakeup)
      newErrors.makeupForLessonId = 'Choose the class this make-up is for';
    if (durationDiffers && !details.staffNote.trim())
      newErrors.staffNote = `The class is ${selectedMakeup!.defaultDurationMins} min — say why this make-up is ${scheduling.duration} min.`;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload: AdminBookOnBehalfInput = {
      studentId: details.studentId,
      instrumentId: details.instrumentId || undefined,
      lessonTypeId: details.lessonTypeId || undefined,
      reason: details.reason,
      notes: details.notes || undefined,
      scheduledDate: format(scheduling.selectedDate!, 'yyyy-MM-dd'),
      scheduledTime: scheduling.selectedTime,
      duration: scheduling.duration,
      teacherId: scheduling.teacherId,
      roomId: scheduling.roomId || undefined,
      // Only send the flag when there's genuinely a credit to spend.
      useMakeupCredit: useCredit,
      makeupForLessonId: useCredit ? selectedMakeup?.lessonId : undefined,
      staffNote: details.staffNote.trim() || undefined,
      shareNoteWithFamily: details.staffNote.trim() ? details.shareNoteWithFamily : false,
    };
    bookMutation.mutate(payload, { onSuccess: handleClose });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Book Extra Class</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'grid', gap: 2 }}>
          <Autocomplete
            options={students.filter((s: Student) => s.isActive)}
            getOptionLabel={(s: Student) =>
              `${s.firstName} ${s.lastName}${s.Family?.name ? ` (${s.Family.name})` : ''}`
            }
            value={selectedStudent}
            onChange={(_, student) => {
              // A make-up belongs to ONE student's class: a new student starts
              // the credit choice again (/qa Phase 6 M1 — the old class id
              // otherwise stayed, with a blank picker and an enabled Book button).
              setDetails((prev) => ({
                ...prev,
                studentId: student?.id || '',
                useMakeupCredit: false,
                makeupForLessonId: '',
                // …and the note: a note shared for one child must never go to
                // another family because the admin missed it (/qa L2).
                staffNote: '',
                shareNoteWithFamily: false,
              }));
              setErrors((prev) => ({ ...prev, studentId: '', makeupForLessonId: '' }));
            }}
            disabled={bookMutation.isPending}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Student"
                required
                error={!!errors.studentId}
                helperText={errors.studentId}
              />
            )}
            fullWidth
          />

          {/* Make-up credit toggle — only shown once a student is selected */}
          {details.studentId && (
            <Box sx={{ mt: -1 }}>
              {creditsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Checking make-up credits…
                  </Typography>
                </Box>
              ) : (
                <Tooltip
                  title={hasCredits ? '' : 'No credits available'}
                  disableHoverListener={hasCredits}
                  arrow
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={details.useMakeupCredit && hasCredits}
                          onChange={(e) =>
                            setDetails((prev) => ({
                              ...prev,
                              useMakeupCredit: e.target.checked,
                              makeupForLessonId: e.target.checked ? prev.makeupForLessonId : '',
                            }))
                          }
                          disabled={!hasCredits || bookMutation.isPending}
                        />
                      }
                      label={
                        hasCredits
                          ? 'Use a make-up credit'
                          : 'Use a make-up credit — no credits available'
                      }
                    />
                  </span>
                </Tooltip>
              )}
            </Box>
          )}

          {/* The class being made up: its TERM is charged, and its length is the
              default — which is what allows a booking in the school holidays. */}
          {details.useMakeupCredit && hasCredits && (
            <Autocomplete
              options={makeupOptions}
              getOptionLabel={makeupOptionLabel}
              getOptionDisabled={(o) => o.creditsRemaining <= 0}
              isOptionEqualToValue={(a, b) => a.lessonId === b.lessonId}
              value={selectedMakeup}
              onChange={(_, option) => selectMakeupClass(option)}
              disabled={bookMutation.isPending}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Making up for"
                  required
                  error={!!errors.makeupForLessonId}
                  helperText={
                    errors.makeupForLessonId ||
                    (selectedMakeup
                      ? `The credit comes from ${selectedMakeup.termName}${
                          selectedMakeup.creditValidUntil
                            ? ` and can be used until ${ddmmyyyy(selectedMakeup.creditValidUntil)}`
                            : ''
                        }. Credits are shared by all of ${selectedMakeup.termName}'s classes.`
                      : undefined)
                  }
                />
              )}
              fullWidth
            />
          )}

          <Autocomplete
            options={instruments.filter((i: Instrument) => i.isActive)}
            getOptionLabel={(i: Instrument) => i.name}
            value={selectedInstrument}
            onChange={(_, instrument) =>
              setDetails((prev) => ({ ...prev, instrumentId: instrument?.id || '' }))
            }
            disabled={bookMutation.isPending}
            renderInput={(params) => (
              <TextField {...params} label="Instrument (optional)" />
            )}
            fullWidth
          />
          <Autocomplete
            options={lessonTypes.filter((lt: LessonType) => lt.isActive)}
            getOptionLabel={(lt: LessonType) => lt.name}
            value={selectedLessonType}
            onChange={(_, lt) =>
              setDetails((prev) => ({ ...prev, lessonTypeId: lt?.id || '' }))
            }
            disabled={bookMutation.isPending}
            renderInput={(params) => (
              <TextField {...params} label="Lesson Type (optional)" />
            )}
            fullWidth
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Reason"
            required
            value={details.reason}
            onChange={(e) => {
              setDetails((prev) => ({ ...prev, reason: e.target.value }));
              setErrors((prev) => ({ ...prev, reason: '' }));
            }}
            error={!!errors.reason}
            // Say what is needed BEFORE submit, not only on rejection: an
            // admin who has picked a past date should see the requirement while
            // they are in the field, rather than meeting it as an error after
            // filling the rest of the form.
            helperText={
              errors.reason ||
              (selectedDateIsPast && details.reason.trim() === DEFAULT_BOOKING_REASON
                ? 'This booking will appear in the retroactive edit report — please say why it is being recorded now.'
                : undefined)
            }
            disabled={bookMutation.isPending}
          />
          {/* ⚠️ This field was always FAMILY-VISIBLE (the parent's bookings page
              shows it) — the label now says so. Staff-only text goes below. */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes (visible to the family)"
            value={details.notes}
            onChange={(e) =>
              setDetails((prev) => ({ ...prev, notes: e.target.value }))
            }
            disabled={bookMutation.isPending}
          />

          {/* ⚠️ `currentTermStart` is passed HERE ONLY — it is what opens the
              date list backwards, and this is the one flow that should record
              history (an admin entering a lesson that was taught but never put
              on the calendar). The two sites in ExtraLessonRequestsPage
              deliberately omit it and stay forward-only: one RESCHEDULES and
              emails the family ("The family will be notified by email"), the
              other schedules a family's own pending request. Neither is a
              record-keeping action, and back-dating them would mail a family
              about a lesson in the past. */}
          <TeacherAvailabilityScheduler
            allowPastDates
            currentTermStart={runningTerm?.startDate ?? null}
            termsForTiles={bookableTerms}
            holidayLastDay={holidayLastDay}
            value={scheduling}
            onChange={setScheduling}
            disabled={bookMutation.isPending}
          />

          {/* Staff-only note: admin + teacher. REQUIRED when a make-up is not the
              class length; reaches the family only if shared. */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Staff note (admin + teacher)"
            required={durationDiffers}
            value={details.staffNote}
            onChange={(e) => {
              const staffNote = e.target.value;
              setDetails((prev) => ({
                ...prev,
                staffNote,
                shareNoteWithFamily: staffNote.trim() ? prev.shareNoteWithFamily : false,
              }));
              setErrors((prev) => ({ ...prev, staffNote: '' }));
            }}
            error={!!errors.staffNote}
            helperText={
              errors.staffNote ||
              (durationDiffers
                ? `The class is ${selectedMakeup!.defaultDurationMins} min — say why this make-up is ${scheduling.duration} min.`
                : 'Not shown to the family unless you share it.')
            }
            disabled={bookMutation.isPending}
          />
          <FormControlLabel
            control={
              <Switch
                checked={details.shareNoteWithFamily}
                onChange={(e) =>
                  setDetails((prev) => ({ ...prev, shareNoteWithFamily: e.target.checked }))
                }
                disabled={!details.staffNote.trim() || bookMutation.isPending}
              />
            }
            label="Share this note with the family"
          />

          {/* The audit consequence, stated where the choice is made. */}
          {selectedDateIsPast && (
            <Alert severity="warning" icon={<HistoryIcon />} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                This date has already passed
              </Typography>
              <Typography variant="body2">
                Booking it records a lesson that already happened rather than scheduling a new
                one. <strong>No confirmation is sent to the family</strong>, and it will appear
                in the <strong>retroactive edit report</strong> against your name.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={bookMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={bookMutation.isPending || !canSubmit}
        >
          {bookMutation.isPending ? 'Booking...' : 'Book Extra Class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
